import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { IMAGES } from "@/lib/api";
import {
  ArrowUpRight,
  ShieldCheck,
  Calculator,
  FileCheck2,
  Timer,
  Cloud,
  Landmark,
  Zap,
  Users2,
  Target,
  BrainCircuit,
  Layers3,
  Sparkles,
  ArrowRight,
  Building2,
  CheckCircle2,
  AlertTriangle,
  Flame,
  MessageCircle,
} from "lucide-react";

// PAS for Payroll — Problem / Agitation / Solution
const PAYROLL_PAS = [
  {
    icon: AlertTriangle,
    tag: "Problem",
    title: "Payroll isn't paperwork. It's exposure.",
    body: "Every month: 20+ statutory filings, changing labour codes, PF/ESI/PT/TDS deductions, bonus and gratuity reconciliations. One missed line item is a compliance notice waiting to happen.",
  },
  {
    icon: Flame,
    tag: "Agitation",
    title: "The cost of getting it wrong is not the fine.",
    body: "It's the audit questions, the CFO's Sunday call, the star engineer whose salary hit her account three days late. Trust breaks quietly — and it doesn't come back with a bonus.",
  },
  {
    icon: ShieldCheck,
    tag: "Solution",
    title: "Managed Payroll on Ravemora infrastructure.",
    body: "We run your entire payroll lifecycle — inputs to salary credit, statutory filings, digitally-signed payslips, MIS dashboards, audit trails, and grievance handling. Your HR team gets a Monday morning report and their calendar back.",
  },
];

// FAB for Payroll — Feature / Advantage / Benefit
const PAYROLL_FAB = [
  {
    icon: Cloud,
    feature: "Cloud-hosted payroll engine + client dashboard",
    advantage: "Single source of truth for HR, Finance and auditors.",
    benefit: "No spreadsheet chaos. Every payslip, every filing — one click away.",
  },
  {
    icon: FileCheck2,
    feature: "Automated PF / ESI / PT / TDS / LWF filings",
    advantage: "Statutory deadlines are engineered into the calendar, not chased.",
    benefit: "Zero late-filing penalties across our full client book — 3 years running.",
  },
  {
    icon: Timer,
    feature: "3-working-day monthly close SLA",
    advantage: "From input freeze to salary credit in 72 hours.",
    benefit: "Predictable payroll = happier employees = lower attrition in month 1.",
  },
  {
    icon: Calculator,
    feature: "Reimbursements, bonus, arrears, F&F workflows",
    advantage: "Every payout scenario is a workflow, not an email chain.",
    benefit: "Finance closes the books faster. Employees get answers, not tickets.",
  },
  {
    icon: Landmark,
    feature: "Audit-ready compliance dossier per period",
    advantage: "ISO 27001-aligned data handling + full immutable audit trail.",
    benefit: "Ministry inspection, statutory audit, due-diligence — all one download.",
  },
  {
    icon: Users2,
    feature: "Dedicated payroll manager per client",
    advantage: "Named human owner, WhatsApp reachable, 4-hour turnaround.",
    benefit: "You always know who owns the answer. No ticketing black holes.",
  },
];

// AIDA + 4C + SLAP for other services — Attention, Interest, Desire, Action
const OTHER_SERVICES = [
  {
    n: "01",
    icon: Target,
    title: "Recruitment & Placement",
    // AIDA: Attention → Interest → Desire → Action
    attention: "Stop paying for empty seats.",
    interest: "Permanent, campus, lateral and high-volume hiring across 12+ industries — with a coaching-led shortlist that actually shows up on day 1.",
    metric: "Avg. 18-day time-to-hire · 94% joiner rate",
    action: "Share a role brief",
  },
  {
    n: "02",
    icon: BrainCircuit,
    title: "Executive Search",
    attention: "The best leaders don't respond to job posts.",
    interest: "Retained, research-driven CXO and functional-head mandates — confidential, mapped, reference-diligenced.",
    metric: "8-week close on average · 100% confidentiality NDA",
    action: "Start a mandate",
  },
  {
    n: "03",
    icon: Layers3,
    title: "Workforce Solutions & RPO",
    attention: "Scale up in weeks. Down in days.",
    interest: "Contract staffing, project workforces and full RPO — deployed on Ravemora payroll with day-one compliance and lifecycle management.",
    metric: "500+ FTEs deployed · 3 industries active",
    action: "Model your workforce",
  },
  {
    n: "04",
    icon: Sparkles,
    title: "HR Consulting & L&D",
    attention: "HR strategy that survives the boardroom.",
    interest: "Org design, HR policy, performance frameworks, employer branding and customized leadership programs.",
    metric: "12+ engagements · Fortune 500 alumni consultants",
    action: "Talk to a consultant",
  },
];

const TRUST_METRICS = [
  { k: "0", v: "late statutory filings — 3 years running" },
  { k: "72h", v: "input-freeze to salary credit" },
  { k: "12", v: "industries served across India & GCC" },
  { k: "500+", v: "employees on Ravemora payroll monthly" },
];

const BookDemoCTA = ({ testid = "demo-btn", className = "btn-cream" }) => (
  <Link
    to="/contact?intent=demo"
    className={`${className} inline-flex items-center gap-2`}
    data-testid={testid}
  >
    Book a Demo <ArrowUpRight size={16} />
  </Link>
);

export default function ForEmployers() {
  const WA_HERO = `https://wa.me/917483500550?text=${encodeURIComponent(
    "Hi Ravemora, I'm interested in your payroll and compliance services. Please share the services, process and pricing."
  )}`;
  return (
    <div className="pb-24" data-testid="for-employers-page">
      <Seo
        path="/payroll"
        exactTitle="Payroll & Compliance Services | Ravemora Global"
        image="https://ravemora.com/og/og-payroll.jpg"
        description="Simplify payroll and compliance with Ravemora Global’s managed payroll services, helping businesses manage employee payroll accurately and efficiently."
      />

      {/* HERO — SLAP + Attention */}
      <section className="relative overflow-hidden bg-navy text-offwhite grain">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
          <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-gold blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full bg-offwhite/40 blur-3xl" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-32 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 label-eyebrow text-gold border border-gold/40 px-3 py-1.5">
                <Building2 size={12} /> Payroll Management &amp; Compliance
              </div>
              <h1 className="serif-display text-5xl md:text-6xl lg:text-[5.5rem] leading-[0.95] mt-8">
                Payroll, precise. <br />
                Hiring, <span className="text-gold">coaching-led.</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl text-offwhite/80 max-w-2xl leading-relaxed">
                Ravemora runs your workforce backbone — managed payroll and statutory compliance, coaching-led recruitment, executive search, RPO and HR consulting. One partner. Twelve industries. Zero late filings.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <BookDemoCTA testid="hero-book-demo" className="btn-cream" />
                <a
                  href={WA_HERO}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-light inline-flex items-center justify-center gap-2"
                  data-testid="hero-whatsapp"
                >
                  <MessageCircle size={14}/> WhatsApp us
                </a>
              </div>
              <p className="mt-6 text-xs uppercase tracking-[0.24em] text-offwhite/40">
                30-minute demo · No obligation · Delivered by a payroll consultant, not a sales rep
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="grid grid-cols-2 gap-4">
                {TRUST_METRICS.map((t) => (
                  <div key={t.v} className="border border-gold/25 bg-white/[0.03] p-5">
                    <div className="serif-display text-3xl md:text-4xl text-gold leading-none">{t.k}</div>
                    <div className="text-xs text-offwhite/70 mt-2 leading-snug">{t.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAYROLL SPOTLIGHT — PAS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24" data-testid="payroll-pas">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="label-eyebrow text-gold">Flagship Service · Managed Payroll</div>
            <h2 className="serif-display text-4xl md:text-6xl mt-4 text-navy leading-tight max-w-3xl">
              Payroll isn't a cost centre. <span className="text-gold">It's a trust contract.</span>
            </h2>
          </div>
          <p className="text-navy/70 max-w-md leading-relaxed text-sm">
            We took the messiest, most-regulated function in HR and rebuilt it as a service — so your team can hire, coach, and grow.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {PAYROLL_PAS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.tag} className={`relative border p-8 ${i === 2 ? "bg-navy text-offwhite border-gold/40" : "bg-white border-navy/10"}`} data-testid={`payroll-pas-${i+1}`}>
                <div className={`absolute -top-4 left-6 px-3 py-1 text-[10px] uppercase tracking-[0.28em] ${i === 2 ? "bg-gold text-white" : "bg-navy text-gold"}`}>
                  {p.tag}
                </div>
                <Icon size={22} className={i === 2 ? "text-gold" : "text-navy/40"} />
                <h3 className={`serif-display text-2xl mt-5 leading-tight ${i === 2 ? "text-offwhite" : "text-navy"}`}>
                  {p.title}
                </h3>
                <p className={`mt-4 leading-relaxed text-sm ${i === 2 ? "text-offwhite/80" : "text-navy/70"}`}>{p.body}</p>
                {i === 2 && (
                  <div className="mt-6">
                    <BookDemoCTA testid="pas-book-demo" className="btn-cream" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* PAYROLL FAB */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24" data-testid="payroll-fab">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="label-eyebrow text-gold">Inside Managed Payroll</div>
            <h2 className="serif-display text-3xl md:text-5xl mt-4 text-navy leading-tight">
              Six pillars. <br />
              <span className="text-gold">One monthly rhythm.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-navy/70 leading-relaxed">
              Every Ravemora payroll engagement runs on the same operating rhythm — so scale, compliance and quality don't degrade as your team grows.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {PAYROLL_FAB.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.feature} className="border border-navy/10 bg-white p-6 hover:border-gold transition-colors" data-testid={`payroll-fab-${i+1}`}>
                <div className="flex items-center justify-between">
                  <Icon size={22} className="text-gold" />
                  <div className="serif-display text-navy/20 text-3xl leading-none">0{i+1}</div>
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-navy/40">Feature</div>
                <h3 className="serif-display text-lg text-navy mt-1 leading-tight">{f.feature}</h3>
                <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-navy/40">Advantage</div>
                <p className="text-navy/75 text-sm mt-1 leading-snug">{f.advantage}</p>
                <div className="mt-4 pt-3 border-t border-navy/10 text-[10px] uppercase tracking-[0.22em] text-gold">Benefit</div>
                <p className="text-navy text-sm mt-1 leading-snug font-medium">{f.benefit}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-navy/10 pt-8">
          <div className="serif-display text-2xl md:text-3xl text-navy max-w-xl leading-snug">
            Ready to see your first month, before you sign?
          </div>
          <BookDemoCTA testid="fab-book-demo" className="btn-primary" />
        </div>
      </section>

      {/* OTHER SERVICES — AIDA / 4C */}
      <section className="bg-navy text-offwhite mt-28 py-24 grain" data-testid="other-services">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="label-eyebrow text-gold">Full Practice Portfolio</div>
              <h2 className="serif-display text-4xl md:text-6xl mt-4 leading-tight">
                Beyond payroll. <br />
                <span className="text-gold">Your full workforce backbone.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-offwhite/75 leading-relaxed">
                Every practice is delivered by senior consultants — not gig sourcers, not offshore call-centres. Clear, credible, and priced against measurable business outcomes.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-16">
            {OTHER_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="border border-gold/25 bg-white/[0.03] p-8 hover:border-gold transition-colors" data-testid={`other-svc-${s.n}`}>
                  <div className="flex items-start justify-between">
                    <div className="serif-display text-4xl text-gold leading-none">{s.n}</div>
                    <Icon size={26} className="text-offwhite/50" />
                  </div>
                  <h3 className="serif-display text-2xl md:text-3xl text-offwhite mt-6 leading-tight">
                    {s.attention}
                  </h3>
                  <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-gold">{s.title}</div>
                  <p className="mt-3 text-offwhite/75 text-sm leading-relaxed">{s.interest}</p>
                  <div className="mt-5 pt-4 border-t border-offwhite/15 flex items-center justify-between gap-4">
                    <span className="text-offwhite/60 text-xs">{s.metric}</span>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-1.5 text-gold hover:text-offwhite label-eyebrow text-[10px]"
                      data-testid={`other-svc-cta-${s.n}`}
                    >
                      {s.action} <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US — Credibility strip */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28" data-testid="why-us">
        <div className="grid lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Compliance-first", d: "Every process is auditor-ready before it goes live." },
            { icon: Zap, t: "Coaching-led hiring", d: "Every candidate we place is mentored on communication + craft." },
            { icon: BrainCircuit, t: "AI-augmented", d: "Automated resume scoring on every application, right out of the box." },
            { icon: Users2, t: "Named ownership", d: "One dedicated consultant per engagement — not a ticket queue." },
          ].map((w, i) => {
            const Icon = w.icon;
            return (
              <div key={i} className="border border-navy/10 bg-white p-6" data-testid={`why-us-${i+1}`}>
                <Icon size={22} className="text-gold" />
                <div className="serif-display text-xl text-navy mt-4">{w.t}</div>
                <div className="text-navy/70 text-sm mt-2 leading-relaxed">{w.d}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-28" data-testid="final-cta">
        <div className="bg-navy text-offwhite p-10 md:p-16 border border-gold/30 relative overflow-hidden">
          <div className="absolute -bottom-20 -right-20 w-[380px] h-[380px] rounded-full bg-gold/20 blur-3xl pointer-events-none" />
          <div className="relative z-10 grid md:grid-cols-[2fr_1fr] gap-10 items-end">
            <div>
              <div className="label-eyebrow text-gold">Next Step</div>
              <h2 className="serif-display text-4xl md:text-6xl mt-4 leading-[1.02]">
                See your first payroll cycle, <br />
                <span className="text-gold">before you sign anything.</span>
              </h2>
              <p className="mt-6 text-offwhite/75 max-w-2xl leading-relaxed">
                Book a 30-minute demo. We'll run a dry payroll on 5 sample employees using your inputs, walk you through statutory outputs, and give you an SLA proposal you can share internally.
              </p>
              <div className="mt-3 text-xs uppercase tracking-[0.24em] text-offwhite/50">
                +91 74835 00550 &middot; info@ravemora.com
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <BookDemoCTA testid="final-cta-book-demo" className="btn-cream" />
              <a
                href="https://wa.me/917483500550?text=Hi%20Ravemora%20Global!%20I'd%20like%20to%20discuss%20our%20payroll%20requirements%20and%20explore%20your%20payroll%20management%20services."
                target="_blank"
                rel="noreferrer"
                className="btn-outline-light inline-flex items-center justify-center gap-2"
                data-testid="final-cta-whatsapp"
              >
                WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
